// ---------------------------------------------------------------------------
// "Onde a pessoa está": detecção de app instalado, iOS/Safari, navegador
// embutido, o evento beforeinstallprompt do Android/Chrome e o controle do
// convite "colocar na tela". Reaproveita os detectores de `navegador.ts`.
// ---------------------------------------------------------------------------

import {
  ehIOS,
  ehAndroid,
  ehAppInstalado,
  ehAppEmbutido,
  ehSafariIOSDeVerdade,
} from './navegador';

export { ehIOS, ehAndroid };

// Ícone do app (selo amarelo com a cruz), publicado no Supabase Storage.
// Usado no cabeçalho, no cartão de convite, na página /instalar e no manifesto.
export const ICONE_APP =
  'https://tzzdgiacrttvmklooxek.supabase.co/storage/v1/object/public/icons/icon-192.png';

// O endereço trouxe ?app=1 (start_url do manifesto)? Então a pessoa abriu pelo
// ícone instalado e tratamos como "app", mesmo que o navegador não reporte
// display-mode (alguns WebViews não reportam).
export function parametroApp(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('app') === '1';
  } catch {
    return false;
  }
}

// É um celular/tela de toque? (pointer: coarse) pega celular e tablet;
// o userAgent cobre navegadores que não reportam "coarse".
export function ehCelular(): boolean {
  if (typeof window === 'undefined') return false;
  const toque =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;
  return toque || ehAndroid || ehIOS || ehIPad();
}

// Está rodando como app instalado na tela inicial (standalone) OU o endereço
// veio com ?app=1 (aberto pelo ícone do app)?
export const emApp = (): boolean => ehAppInstalado || parametroApp();

// Dentro de webview de app (Instagram, Facebook, TikTok etc.)?
export const navegadorEmbutido = (): boolean => ehAppEmbutido;

export const ehIPad = (): boolean => /iPad/i.test(navigator.userAgent || '');

// iPhone/iPad no Safari de verdade (sem webview, sem Chrome/Firefox/Edge).
export function ehSafariIOS(): boolean {
  return ehSafariIOSDeVerdade;
}

// ---------------------------------------------------------------------------
// beforeinstallprompt — o Chrome/Edge/Samsung disparam antes de permitir o
// "Instalar". Guardamos o evento num store global para usar no ConviteApp.
// ---------------------------------------------------------------------------

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

let _beforeInstallPrompt: BeforeInstallPromptEvent | null = null;

export function guardarBeforeInstallPrompt(e: Event): void {
  _beforeInstallPrompt = e as BeforeInstallPromptEvent;
}

export function beforeInstallPrompt(): BeforeInstallPromptEvent | null {
  return _beforeInstallPrompt;
}

// Abre o link no navegador de verdade (saindo do webview de app).
export function abrirNoNavegadorExterno(): void {
  const url = window.location.href;
  if (ehAndroid) {
    const semEsquema = url.replace(/^https?:\/\//, '');
    window.location.href =
      'intent://' +
      semEsquema +
      '#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=' +
      encodeURIComponent(url) +
      ';end';
  } else if (ehIOS) {
    window.location.href =
      'x-safari-https://' +
      window.location.host +
      window.location.pathname +
      window.location.search;
  } else {
    window.open(url, '_blank');
  }
}

// ---------------------------------------------------------------------------
// Controle do convite: "agora não" (7 dias), instalado.
// ---------------------------------------------------------------------------

const CHAVE_VISITAS = 'slc_visitas';
const CHAVE_SESSAO_VISITA = 'slc_sessao_visitada';
const CHAVE_AGORA_NAO = 'slc_instalar_agora_nao';
const CHAVE_INSTALADO = 'slc_instalado';
const CHAVE_SESSAO_CONVIDE = 'slc_sessao_convidada';
const CHAVE_SESSAO_EMBUTIDO = 'slc_sessao_embutido';

export function registrarVisita(): number {
  if (!sessionStorage.getItem(CHAVE_SESSAO_VISITA)) {
    sessionStorage.setItem(CHAVE_SESSAO_VISITA, '1');
    const atual = Number.parseInt(localStorage.getItem(CHAVE_VISITAS) || '0', 10) || 0;
    localStorage.setItem(CHAVE_VISITAS, String(atual + 1));
  }
  return Number.parseInt(localStorage.getItem(CHAVE_VISITAS) || '0', 10) || 0;
}

export function numeroDeVisitas(): number {
  return Number.parseInt(localStorage.getItem(CHAVE_VISITAS) || '0', 10) || 0;
}

export function conviteAdiado(): boolean {
  const ate = Number.parseInt(localStorage.getItem(CHAVE_AGORA_NAO) || '0', 10) || 0;
  return Date.now() < ate;
}

export function adiarConvite(dias = 7): void {
  localStorage.setItem(CHAVE_AGORA_NAO, String(Date.now() + dias * 24 * 3600 * 1000));
}

// Ouvintes de "instalado": o botão do cabeçalho e o convite reagem na hora
// quando o app é instalado — somem sem recarregar a página.
let _ouvintesInstalado: Array<() => void> = [];

export function aoAppInstalado(fn: () => void): () => void {
  _ouvintesInstalado.push(fn);
  return () => {
    _ouvintesInstalado = _ouvintesInstalado.filter((f) => f !== fn);
  };
}

export function marcarInstalado(): void {
  localStorage.setItem(CHAVE_INSTALADO, '1');
  setInstalando(false);
  _ouvintesInstalado.forEach((f) => f());
}

export function jaInstalado(): boolean {
  return emApp() || localStorage.getItem(CHAVE_INSTALADO) === '1';
}

// ---------------------------------------------------------------------------
// "Instalando…": entre o prompt aceito e o appinstalled. Impede toque duplo.
// ---------------------------------------------------------------------------

let _instalando = false;
let _ouvintesInstalando: Array<(v: boolean) => void> = [];

export function estaInstalando(): boolean {
  return _instalando;
}

export function aoMudarInstalando(fn: (v: boolean) => void): () => void {
  _ouvintesInstalando.push(fn);
  return () => {
    _ouvintesInstalando = _ouvintesInstalando.filter((f) => f !== fn);
  };
}

function setInstalando(v: boolean): void {
  _instalando = v;
  _ouvintesInstalando.forEach((f) => f(v));
}

// ---------------------------------------------------------------------------
// Instalação: tenta o prompt nativo do navegador. Sem prompt, devolve qual
// instrução mostrar (folha de 2 passos no Android, de 3 passos no iPhone).
// ---------------------------------------------------------------------------

export type InstrucaoInstalar = 'android' | 'ios' | 'ios-outro' | 'outro';

export function tentarInstalar(): 'prompt' | InstrucaoInstalar {
  const evt = beforeInstallPrompt();
  if (evt) {
    _beforeInstallPrompt = null;
    setInstalando(true);
    evt.prompt();
    evt.userChoice
      .then((c) => {
        if (c.outcome === 'accepted') {
          // Aguarda o appinstalled; mantém "Instalando…" até lá.
        } else {
          setInstalando(false);
          adiarConvite();
        }
      })
      .catch(() => {
        setInstalando(false);
      });
    return 'prompt';
  }
  if (ehIOS) return ehSafariIOS() ? 'ios' : 'ios-outro';
  if (ehAndroid) return 'android';
  return 'outro';
}

export function conviteJaMostradoNestaSessao(): boolean {
  return sessionStorage.getItem(CHAVE_SESSAO_CONVIDE) === '1';
}

export function marcarConviteMostrado(): void {
  sessionStorage.setItem(CHAVE_SESSAO_CONVIDE, '1');
}

export function folhaEmbutidoJaMostrada(): boolean {
  return sessionStorage.getItem(CHAVE_SESSAO_EMBUTIDO) === '1';
}

export function marcarFolhaEmbutido(): void {
  sessionStorage.setItem(CHAVE_SESSAO_EMBUTIDO, '1');
}

// ---------------------------------------------------------------------------
// Função pendente: quando alguém abre um link de função (/equipe/<token>) no
// celular SEM o app instalado, guardamos o token aqui. Depois, já dentro do
// app, a tela "Entrar numa função" oferece abrir direto esse link.
// ---------------------------------------------------------------------------

const CHAVE_FUNCAO_PENDENTE = 'seliga:funcao_pendente';

export function guardarFuncaoPendente(token: string): void {
  try {
    localStorage.setItem(CHAVE_FUNCAO_PENDENTE, token);
  } catch {
    // armazenamento indisponível: ignora
  }
}

export function lerFuncaoPendente(): string | null {
  try {
    return localStorage.getItem(CHAVE_FUNCAO_PENDENTE);
  } catch {
    return null;
  }
}

export function limparFuncaoPendente(): void {
  try {
    localStorage.removeItem(CHAVE_FUNCAO_PENDENTE);
  } catch {
    // ignora
  }
}